import * as React from 'react';
import { UnauthorizedException, BusinessLogicError } from '@40seas/errors';
import { AuthContextValue, OAuthLoginResult, LoginResponse, RegistrationResponse } from './types';
import { authClient } from './AuthClient';
import { useAuthState, useAuthInitialization, useAuthTokenListener } from './hooks';

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 600;
const POPUP_TIMEOUT_MS = 120000; // 2 minutes

interface OAuthPopupResult {
    code: string;
    state: string | null;
    provider: string;
}

function openOAuthPopup(url: string, expectedProvider: string): Promise<OAuthPopupResult> {
    return new Promise((resolve, reject) => {
        const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
        const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
        const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no`;

        const popup = window.open(url, 'oauth-popup', features);
        if (!popup) {
            reject(new Error('Popup was blocked. Please allow popups for this site and try again.'));
            return;
        }

        // Declare timer IDs before cleanup() so they're in scope (avoids TDZ with const)
        let settled = false;
        let pollId: ReturnType<typeof setInterval>;
        let timeoutId: ReturnType<typeof setTimeout>;

        const cleanup = () => {
            settled = true;
            clearTimeout(timeoutId);
            clearInterval(pollId);
            window.removeEventListener('message', handleMessage);
        };

        const handleMessage = (event: MessageEvent) => {
            // Verify message came from our popup window (prevents spoofed messages from other windows)
            if (event.source !== popup) return;
            if (!event.data || event.data.type !== 'oauth-callback') return;

            cleanup();

            if (event.data.error) {
                reject(new Error(event.data.errorDescription || event.data.error));
                return;
            }

            if (!event.data.code) {
                reject(new Error('OAuth callback did not include an authorization code'));
                return;
            }

            resolve({
                code: event.data.code,
                state: event.data.state,
                provider: event.data.provider || expectedProvider,
            });
        };

        window.addEventListener('message', handleMessage);

        pollId = setInterval(() => {
            if (!settled && popup.closed) {
                // The popup calls postMessage() before window.close(), so the message
                // should already be queued. Give a short grace period for the event loop
                // to deliver it before treating the close as user cancellation.
                clearInterval(pollId);
                setTimeout(() => {
                    if (!settled) {
                        cleanup();
                        reject(new Error('Authentication cancelled'));
                    }
                }, 1000);
            }
        }, 500);

        timeoutId = setTimeout(() => {
            if (!settled) {
                cleanup();
                popup.close();
                reject(new Error('Authentication timed out. Please try again.'));
            }
        }, POPUP_TIMEOUT_MS);
    });
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

/**
 * Hook to access auth context.
 * Returns a safe default when used outside provider (for conditional rendering scenarios).
 */
export const useAuth = (): AuthContextValue => {
    const context = React.useContext(AuthContext);
    if (!context) {
        // Return a safe default when used outside provider
        return {
            isAuthenticated: false,
            isLoading: false,
            user: null,
            error: null,
            loginWithPassword: async () => {
                throw new Error('Auth provider is not enabled');
            },
            loginWithRedirect: async () => {
                throw new Error('Auth provider is not enabled');
            },
            loginWithGoogle: async () => {
                throw new Error('Auth provider is not enabled');
            },
            loginWithMicrosoft: async () => {
                throw new Error('Auth provider is not enabled');
            },
            register: async () => {
                throw new Error('Auth provider is not enabled');
            },
            resendVerification: async () => {
                throw new Error('Auth provider is not enabled');
            },
            verifyCode: async () => {
                throw new Error('Auth provider is not enabled');
            },
            logout: async () => {
                throw new Error('Auth provider is not enabled');
            },
            getAccessTokenSilently: async () => {
                throw new Error('Auth provider is not enabled');
            },
            clearError: () => {
                // No-op when provider is not enabled
            },
        };
    }
    return context;
};

export interface AuthProviderProps {
    children: React.ReactNode;
    /**
     * Callback called after redirect-based OAuth login completes
     */
    onRedirectCallback?: (appState?: { returnTo?: string }) => void;
    /**
     * Optional loading component to show during auth initialization
     */
    loadingComponent?: React.ReactNode;
}

/**
 * Auth-V2 Provider component.
 * Provides authentication context for applications using the auth-v2 backend.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children, onRedirectCallback, loadingComponent }) => {
    // Auth state management
    const { isLoading, isAuthenticated, user, error, setLoading, setAuthenticated, setError, clearAuth } = useAuthState();

    // Auth initialization (handles OAuth callbacks and initial auth check)
    useAuthInitialization({
        onRedirectCallback,
        onAuthStateChange: setAuthenticated,
        onLoadingChange: setLoading,
        onError: setError,
    });

    // Listen for token changes (e.g., from storage events in other tabs)
    useAuthTokenListener(
        React.useCallback(() => {
            const authenticated = authClient.isAuthenticated();
            const currentUser = authClient.getUser();
            if (authenticated && currentUser) {
                setAuthenticated(true, currentUser);
            }
        }, [setAuthenticated]),
    );

    // OAuth popup-based login methods (matches Auth0's proven popup pattern)
    const loginWithGoogle = React.useCallback(async (): Promise<OAuthLoginResult> => {
        const returnTo = window.location.origin + window.location.pathname;
        const googleAuthUrl = await authClient.getGoogleAuthUrl(returnTo);
        const result = await openOAuthPopup(googleAuthUrl, 'google');
        const response = await authClient.handleGoogleCallback(result.code, result.state || undefined);
        setAuthenticated(true, response.user);
        return { accessToken: response.tokens.accessToken };
    }, [setAuthenticated]);

    const loginWithMicrosoft = React.useCallback(async (): Promise<OAuthLoginResult> => {
        const returnTo = window.location.origin + window.location.pathname;
        const microsoftAuthUrl = await authClient.getMicrosoftAuthUrl(returnTo);
        const result = await openOAuthPopup(microsoftAuthUrl, 'microsoft');
        const response = await authClient.handleMicrosoftCallback(result.code, result.state || undefined);
        setAuthenticated(true, response.user);
        return { accessToken: response.tokens.accessToken };
    }, [setAuthenticated]);

    // Password-based login
    const loginWithPassword = React.useCallback(async (email: string, password: string): Promise<LoginResponse> => {
        return authClient.loginWithPassword(email, password);
    }, []);

    // Registration
    const register = React.useCallback(async (email: string, password: string, name?: string): Promise<RegistrationResponse> => {
        return authClient.register(email, password, name);
    }, []);

    // Resend verification email
    const resendVerification = React.useCallback(async (email: string): Promise<{ message: string }> => {
        return authClient.resendVerification(email);
    }, []);

    // Verify email with 6-digit code and auto-login
    const verifyCode = React.useCallback(async (email: string, code: string): Promise<LoginResponse> => {
        return authClient.verifyCode(email, code);
    }, []);

    // Redirect-based login (uses popup, then calls onRedirectCallback for navigation)
    const loginWithRedirect = React.useCallback(
        async (options?: { appState?: { returnTo?: string } }) => {
            try {
                // Always use the current page's origin for returnTo so the backend derives
                // the correct targetOrigin for postMessage. Relative paths (e.g. "/dashboard")
                // would be resolved against FRONTEND_URL on the backend, which may differ from
                // the actual page origin (e.g. dashboard.40seas.com vs app.40seas.com).
                const appReturnTo = options?.appState?.returnTo;
                const returnTo = appReturnTo
                    ? new URL(appReturnTo, window.location.origin).toString()
                    : window.location.origin + window.location.pathname;
                const googleAuthUrl = await authClient.getGoogleAuthUrl(returnTo);
                const result = await openOAuthPopup(googleAuthUrl, 'google');
                const response = await authClient.handleGoogleCallback(result.code, result.state || undefined);
                setAuthenticated(true, response.user);

                // Call onRedirectCallback to handle navigation (matches Auth0 pattern)
                if (onRedirectCallback) {
                    onRedirectCallback(options?.appState);
                }
            } catch (err) {
                throw new BusinessLogicError(
                    `Failed to complete OAuth login: ${err instanceof Error ? err.message : 'Unknown error'}`,
                    'OAUTH_LOGIN_FAILED',
                );
            }
        },
        [setAuthenticated, onRedirectCallback],
    );

    // Logout
    const logout = React.useCallback(
        async (options?: { returnTo?: string }) => {
            try {
                await authClient.logout();
            } catch {
                // Ignore logout errors
            }
            clearAuth();

            if (options?.returnTo) {
                window.location.href = options.returnTo;
            }
        },
        [clearAuth],
    );

    // Get access token (with optional refresh)
    const getAccessTokenSilently = React.useCallback(
        async (options?: { ignoreCache?: boolean }): Promise<string> => {
            if (options?.ignoreCache || authClient.isTokenExpired()) {
                try {
                    await authClient.refreshToken();
                } catch (err) {
                    clearAuth();
                    throw new UnauthorizedException({
                        message: `Failed to refresh token: ${err instanceof Error ? err.message : 'Unknown error'}`,
                        code: 'TOKEN_REFRESH_FAILED',
                    });
                }
            }

            const token = authClient.getAccessToken();
            if (!token) {
                throw new Error('No access token available');
            }
            return token;
        },
        [clearAuth],
    );

    // Clear error callback
    const clearError = React.useCallback(() => {
        setError(null);
    }, [setError]);

    const contextValue: AuthContextValue = React.useMemo(
        () => ({
            isAuthenticated,
            isLoading,
            user,
            error,
            loginWithPassword,
            loginWithRedirect,
            loginWithGoogle,
            loginWithMicrosoft,
            register,
            resendVerification,
            verifyCode,
            logout,
            getAccessTokenSilently,
            clearError,
        }),
        [
            isAuthenticated,
            isLoading,
            user,
            error,
            loginWithPassword,
            loginWithRedirect,
            loginWithGoogle,
            loginWithMicrosoft,
            register,
            resendVerification,
            verifyCode,
            logout,
            getAccessTokenSilently,
            clearError,
        ],
    );

    if (isLoading && loadingComponent) {
        return loadingComponent as React.ReactElement;
    }

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
